import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { LoaderBar, PageHeader, FlexRowBetween, FlexRow } from 'components'
import { DesktopMode, MobileMode } from 'layout'
import { Input } from 'semantic-ui-react'
import { AppDropdown as Dropdown } from 'components/AppDropdown'
import { CaretLeft } from 'phosphor-react'
import styled from 'styled-components'
import { useAssetActivity } from 'hooks/asset-manager'
import { AssetAuditLogList } from './../../page-components/asset/audit-log/List';

const AuditLogContainer = styled.div`
    padding: 20px;
    margin-top: 20px;
`

export const AssetAuditLogScreen = () => {
    const navigate = useNavigate()
    const { activities, loading, fetchActivities } = useAssetActivity()
    const [data, setData] = useState([])
    const [searchText, setSearchText] = useState('')
    const [sortBy, setSortBy] = useState('newest')
    const [filter, setFilter] = useState({
        PageNumber: 1,
        PageSize: 5,
    })
    const [totalCount, setTotalCount] = useState(0)

    useEffect(() => {
        fetchActivities(null, sortBy)
    }, [fetchActivities, sortBy])

    useEffect(() => {
        const allLogs = activities.map(item => ({
            id: item.id,
            date: item.createdOn,
            user: item.userName || 'Current User',
            action: item.action || '',
            relatedAsset: item.assetName || item.assetTag || '',
        }))
        let filtered = allLogs

        if (searchText) {
            const searchLower = searchText.toLowerCase()
            filtered = filtered.filter(
                log =>
                    log.user.toLowerCase().includes(searchLower) ||
                    log.action.toLowerCase().includes(searchLower) ||
                    (log.relatedAsset &&
                        log.relatedAsset.toLowerCase().includes(searchLower)),
            )
        }

        const startIndex = (filter.PageNumber - 1) * filter.PageSize
        const endIndex = startIndex + filter.PageSize
        const paginatedData = filtered.slice(startIndex, endIndex)
        setData(paginatedData)
        setTotalCount(filtered.length)
    }, [activities, filter.PageNumber, filter.PageSize, searchText])

    const pageChanged = (newFilter) => {
        setFilter(newFilter)
    }

    const handleSearchChange = (e) => {
        setSearchText(e.target.value)
        setFilter(prev => ({ ...prev, PageNumber: 1 }))
    }

    return (
        <AuditLogContainer>
            <DesktopMode>
                <FlexRowBetween className="mb-3">
                    <PageHeader header="Asset Audit Log" />
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <Dropdown
                            selection
                            options={[
                                { key: 'newest', text: 'Newest first', value: 'newest' },
                                { key: 'oldest', text: 'Oldest first', value: 'oldest' },
                            ]}
                            value={sortBy}
                            onChange={(_, { value }) =>
                                setSortBy(value || 'newest')
                            }
                        />
                    </div>
                </FlexRowBetween>

                <div className="mb-3">
                    <Input
                        icon="search"
                        iconPosition="left"
                        placeholder="Search audit logs"
                        value={searchText}
                        onChange={handleSearchChange}
                        style={{ maxWidth: '400px' }}
                    />
                </div>
            </DesktopMode>

            <MobileMode>
                <div className="mb-3">
                    <FlexRowBetween className="mb-3">
                        <FlexRow
                            onClick={() => navigate('/app/asset-management')}
                            className="align-items-center pointer"
                        >
                            <CaretLeft size={24} />
                        </FlexRow>
                    </FlexRowBetween>

                    <h2 style={{ marginBottom: '8px', fontWeight: 600 }}>
                        Asset Audit Log
                    </h2>

                    <div className="mb-3">
                        <Input
                            icon="search"
                            iconPosition="left"
                            placeholder="Search audit logs"
                            value={searchText}
                            onChange={handleSearchChange}
                            fluid
                        />
                    </div>
                </div>
            </MobileMode>

            <div className="my-3">
                {loading && <LoaderBar content="Loading audit logs..." />}
                {!loading && totalCount === 0 && (
                    <div style={{ color: '#666', marginBottom: '16px' }}>
                        Audit log API is not available yet. This screen will stay
                        empty until backend provides an asset audit-log endpoint.
                    </div>
                )}
                {!loading && (
                    <AssetAuditLogList
                        data={data}
                        filter={filter}
                        pageChanged={pageChanged}
                        totalCount={totalCount}
                    />
                )}
            </div>
        </AuditLogContainer>
    )
}
